import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, LogIn, Loader2 } from "lucide-react";
import { toast } from "../components/ui/sonner";
import { useMobileAuth } from "../context/MobileAuthContext";

export default function Login() {
  const { login, isAuthed } = useMobileAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthed) navigate("/dashboard");
  }, [isAuthed, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast("Please enter your email and password.");
      return;
    }
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast("Welcome back!");
      navigate("/dashboard");
    } catch (err) {
      toast("Login failed", { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen bg-[#0A192F] text-slate-100 antialiased flex flex-col"
    >
      <header className="bg-black border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 md:px-8 h-[72px] flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src="/geonetix-logo-full.webp" alt="GeoNetix" className="h-10 md:h-12 w-auto object-contain" />
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-[#00B4D8]">
            <ArrowLeft size={16} /> Home
          </Link>
        </div>
      </header>

      <div className="flex-1 relative flex items-center justify-center px-6 py-16">
        <div className="absolute inset-0 gx-grid-bg opacity-30" />
        <div className="absolute -top-20 left-1/2 -translate-x-1/2 h-72 w-[70%] rounded-full bg-[#00B4D8]/10 blur-[120px]" />

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-full max-w-md rounded-3xl border border-white/12 bg-[#112240] p-8 md:p-10"
        >
          <p className="font-mono-gx text-xs uppercase tracking-[0.25em] text-[#00B4D8] mb-3">
            / Member Login
          </p>
          <h1 className="font-display font-black text-3xl tracking-tight mb-2">
            Access your <span className="gx-text-gradient">wallet.</span>
          </h1>
          <p className="text-sm text-slate-400 mb-8">
            Log in with your GeoNetix app account to view earnings and withdraw in INR.
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <label className="block">
              <span className="mb-2 block text-sm font-medium text-slate-300">Email</span>
              <input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#00B4D8]/60"
              />
            </label>
            <label className="block">
              <span className="mb-2 block text-sm font-medium text-slate-300">Password</span>
              <input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#00B4D8]/60"
              />
            </label>
            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-[#4CAF50] px-7 py-4 text-[15px] font-semibold text-[#04140a] hover:brightness-110 transition-[filter,transform] duration-200 hover:-translate-y-0.5 gx-green-glow disabled:opacity-60"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <LogIn size={18} />}
              {loading ? "Signing in..." : "Log In"}
            </button>
          </form>

          <p className="mt-6 text-xs text-slate-500 text-center">
            Don&apos;t have an account? Download the GeoNetix app to sign up.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
